/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { DayOfWeek } from "@fluentui/react";
import * as moment from "moment";

import { DEFAULT_START_OF_WEEK } from "../../staffhub/StaffHubConstants";
import { ScheduleCalendarTypes } from "../../staffhub/sh-models";

import { GetTimestampLimitForViewParams } from "./GetTimestampLimitForViewParams";
import { IPastDateLimitManager } from "./IPastDateLimitManager";
import { dateLimitsList } from "./dateLimitsList";

import { PastDateLimitOption } from ".";

const oneDayInMs = 86400000;
const defaultTimezone = "Etc/UTC";

/**
 * Manager for past date limits.
 */
export class PastDateLimitManager implements IPastDateLimitManager {
    /**
     * Calculates the limit of how far back the user can see the schedule.
     * @param pastLimitInDays Limit in days.
     * @param date The current time.
     * @returns The unix timestamp limit in milliseconds.
     */
    private calculatePastTimestampLimit(pastLimitInDays: number, date: moment.Moment): number {
        // date limit timestamp calculated by subtracting current timestamp by `limitInDays`
        return date.valueOf() - pastLimitInDays * oneDayInMs;
    }

    /**
     * Calculates the limit of how far back the user can see the schedule based  on the view type.
     * @param params Parameters.
     * @returns The unix timestamp limit in milliseconds.
     */
    public getTimestampLimitForView(params: GetTimestampLimitForViewParams): number {
        const { date, pastDateLimitInDays, view, startDayOfWeek } = params;
        params.startDayOfWeek = startDayOfWeek ? startDayOfWeek : DayOfWeek[DEFAULT_START_OF_WEEK]; // If null startDayOfWeek, it will use DEFAULT_START_OF_WEEK (Sunday)

        const absoluteTimestampLimit = this.calculatePastTimestampLimit(pastDateLimitInDays, date);
        const dayTimestampLimit = this.calculateNearestCompleteDay(params, absoluteTimestampLimit);

        switch (view) {
            case ScheduleCalendarTypes.Day:
                return dayTimestampLimit;

            case ScheduleCalendarTypes.Week:
            case ScheduleCalendarTypes.Fortnight:
                return this.calculateNearestCompleteWeek(params, dayTimestampLimit);

            case ScheduleCalendarTypes.Month:
                return this.calculateNearestCompleteMonth(params, dayTimestampLimit);
            default:
                return;
        }
    }

    /**
     * Calculates the nearest complete day for the day view limit.
     * E.g. Aug 15th 00:00 => Aug 15th 00:00; Aug 15th 09:00 => Aug 16th 00:00.
     * @param params Parameters.
     * @param pastTimestampLimit The past limit timestamp.
     * @returns The unix timestamp limit in milliseconds.
     */
    private calculateNearestCompleteDay(params: GetTimestampLimitForViewParams, pastTimestampLimit: number): number {
        const { timeZone } = params;

        const dayViewLimit = moment.tz(pastTimestampLimit, !moment.tz.zone(timeZone) ? defaultTimezone : timeZone);

        // go to next day if timestamp is not midnight - check if hour, minute, and second are equal
        if (dayViewLimit.format("HH:mm:ss") !== "00:00:00") {
            dayViewLimit.add(1, "days");
        }

        return dayViewLimit.startOf("day").valueOf();
    }

    /**
     * Calculates the nearest complete week for the week view limit.
     * E.g. Sun Aug 15th 00:00 => Sun Aug 15th 00:00; Fri Aug 15th 00:00 => Sun Aug 17th 00:00.
     * @param params Parameters.
     * @param dayViewLimitUnix The day view limit in unix.
     * @returns The unix timestamp limit in milliseconds.
     */
    private calculateNearestCompleteWeek(params: GetTimestampLimitForViewParams, dayViewLimitUnix: number): number {
        const { timeZone, startDayOfWeek } = params;

        const dayLimitInMoment = moment.tz(dayViewLimitUnix, !moment.tz.zone(timeZone) ? defaultTimezone : timeZone);
        const nearestStartOfWeek = dayLimitInMoment.clone();
        nearestStartOfWeek.day(startDayOfWeek);

        // go to next day if timestamp is not midnight
        if (dayLimitInMoment.isAfter(nearestStartOfWeek, "day")) {
            nearestStartOfWeek.add(1, "weeks");
        }

        return nearestStartOfWeek.startOf("day").valueOf();
    }

    /**
     * Calculates the nearest complete month for the month view limit.
     * E.g. Aug 1st 00:00 => Aug 1st 00:00; Aug 15th 00:00 => Sept 1st 00:00.
     * @param params Parameters.
     * @param dayViewLimitUnix The day view limit in unix.
     * @returns The unix timestamp limit in milliseconds.
     */
    private calculateNearestCompleteMonth(params: GetTimestampLimitForViewParams, dayViewLimitUnix: number): number {
        const { timeZone } = params;

        const dayLimitInMoment = moment.tz(dayViewLimitUnix, !moment.tz.zone(timeZone) ? defaultTimezone : timeZone);
        const startOfMonth = dayLimitInMoment.clone().startOf("month");

        // Go to next month if timestamp is not on the first day of the month
        if (dayLimitInMoment.isAfter(startOfMonth, "day")) {
            startOfMonth.add(1, "month");
        }

        return startOfMonth.startOf("day").valueOf();
    }

    /** Gets list of all options of months and the corresponding number of days.
     * @returns List of months and corresponding number of days.
     */
    public getPastDateLimitOptions(): PastDateLimitOption[] {
        return dateLimitsList;
    }
}
